// =============================================================================
// VandieZ shipper-web -- Application root
// =============================================================================
// Wires the auth provider + router. The authenticated surface mounts the
// AppShell layout (sidebar + theme + toasts) once; every protected page
// renders as a nested <Outlet/> child.
//
// Routes:
//
//   /login               -> LoginPage          (public)
//
//   (inside AppShell)
//     /                  -> MyLoadsPage           - landing
//     /loads/new         -> PostLoadPage
//     /loads/:loadId     -> LoadDetailPage        - bids, accept, cancel, edit
//     /trips/:tripId     -> TripDetailPage        - live tracking
//     /wallet            -> WalletPage            - balance + ledger
//     /vehicles          -> VehiclesPage          - my fleet
//     /vehicles/:id      -> VehicleDetailPage     - edit / online
//     /kyc               -> KycPage               - upload + status
//     /profile           -> ProfilePage           - identity + business
//     /bank-accounts     -> BankAccountsPage      - list / add / primary
//     /support           -> SupportTicketsPage    - list + create
//     /support/:ticketId -> SupportTicketDetailPage
//     /disputes          -> DisputesPage          - per-trip disputes
//     /disputes/:tripId  -> DisputeDetailPage
// =============================================================================

import type React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AppShell } from "@/components/AppShell";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthProvider } from "@/lib/auth";
import { SidebarCollapseProvider, ThemeProvider } from "@/lib/theme";
import { ToastProvider } from "@/lib/toast";
import { BankAccountsPage } from "@/pages/BankAccountsPage";
import { AddressesPage } from "@/pages/AddressesPage";
import { DisputeDetailPage } from "@/pages/DisputeDetailPage";
import { DisputesPage } from "@/pages/DisputesPage";
import { InboxPage } from "@/pages/InboxPage";
import { KycPage } from "@/pages/KycPage";
import { LoadDetailPage } from "@/pages/LoadDetailPage";
import { LoginPage } from "@/pages/LoginPage";
import { MyLoadsPage } from "@/pages/MyLoadsPage";
import { PostLoadPage } from "@/pages/PostLoadPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { RatingsPage } from "@/pages/RatingsPage";
import { SupportTicketDetailPage } from "@/pages/SupportTicketDetailPage";
import { SupportTicketsPage } from "@/pages/SupportTicketsPage";
import { TripDetailPage } from "@/pages/TripDetailPage";
import { VehicleDetailPage } from "@/pages/VehicleDetailPage";
import { VehiclesPage } from "@/pages/VehiclesPage";
import { WalletPage } from "@/pages/WalletPage";

export function App(): React.JSX.Element {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <SidebarCollapseProvider>
          <ToastProvider>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </ToastProvider>
        </SidebarCollapseProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

function AppRoutes(): React.JSX.Element {
  return (
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<MyLoadsPage />} />
            <Route path="/loads/new" element={<PostLoadPage />} />
            <Route path="/loads/:loadId" element={<LoadDetailPage />} />
            <Route path="/trips/:tripId" element={<TripDetailPage />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/vehicles" element={<VehiclesPage />} />
            <Route path="/vehicles/:vehicleId" element={<VehicleDetailPage />} />
            <Route path="/kyc" element={<KycPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/bank-accounts" element={<BankAccountsPage />} />
            <Route path="/addresses" element={<AddressesPage />} />
            <Route path="/support" element={<SupportTicketsPage />} />
            <Route path="/support/:ticketId" element={<SupportTicketDetailPage />} />
            <Route path="/disputes" element={<DisputesPage />} />
            <Route path="/disputes/:tripId" element={<DisputeDetailPage />} />
            <Route path="/ratings" element={<RatingsPage />} />
            <Route path="/inbox" element={<InboxPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}
